/**
 * A regra de quando algo com validade passa a ser anunciado.
 *
 * Papel: aqui ficam `JANELA_DE_VENCIMENTO_EM_DIAS`, o tipo `ItemVencendo` e
 * `apurarVencimentos`, que reduz qualquer lista de coisas com data de validade
 * ao que precisa aparecer no aviso de vencimento, e as três peças de texto e
 * ordem que o `PopupVencimentos` reparte com quem o abre —
 * `ordenarPorUrgencia`, `tituloDoAviso` e `prazoPorExtenso`.
 *
 * Como usar: um módulo cujos registros vençam mapeia cada um para
 * `EntradaDeVencimento` — identificador, a quem pertence, como se chama no
 * aviso, a validade e a tela onde se resolve — e chama `apurarVencimentos` com
 * a janela que vale para aquele registro. A contagem de dias, o descarte de
 * quem não tem validade e a inclusão do que já venceu moram aqui e em lugar
 * nenhum mais; um módulo que os reescrevesse passaria a discordar dos outros
 * sem nada acusar, e o aviso mostraria dois critérios na mesma lista.
 *
 * A janela, ao contrário, é de cada módulo, e por isso chega por parâmetro.
 * Documentos pessoais usam `JANELA_DE_VENCIMENTO_EM_DIAS`. As rotinas
 * preventivas têm a janela delas, mais curta e declarada no próprio módulo de
 * manutenções, e o plano de saúde tem uma janela negativa, declarada no módulo
 * de saúde, que só deixa entrar a carteirinha já vencida: alargar ou encurtar
 * `JANELA_DE_VENCIMENTO_EM_DIAS` não mexe no aviso das preventivas nem no das
 * carteirinhas, e vice-versa.
 *
 * O registro que entra com `lembrete` — a carteirinha vencida — tem a linha
 * escrita de outro jeito: o lembrete no lugar do prazo, a validade anterior
 * logo depois dele, e nada no fim. Quem escreve a linha é o
 * `PopupVencimentos`, e a regra de escrita fica em um só lugar.
 *
 * `hojeIso` vem por parâmetro, e não do relógio. É o que torna o aviso
 * testável: um teste que lesse a data de hoje passaria agora e falharia sozinho
 * num dia qualquer, sem que nada tivesse mudado no código.
 *
 * A lista devolvida por `apurarVencimentos` **não vem ordenada**. Quem ordena
 * é quem desenha a lista final, por `ordenarPorUrgencia`: com mais de um
 * módulo no aviso, ordenar em cada apuração daria listas certas e uma
 * concatenação fora de ordem.
 *
 * A caixa que abre ao carregar a página e a que o sino abre mostram a mesma
 * lista, e por isso o título, a ordem e a frase de prazo saem daqui, e não de
 * cada componente: escritos em dois lugares, as duas aberturas acabariam
 * anunciando o mesmo item com palavras ou posições diferentes.
 */

/**
 * Quantos dias antes do vencimento um documento pessoal passa a ser anunciado.
 *
 * São 183 dias, cerca de seis meses: o passaporte que vence em 31 de dezembro
 * entra no aviso em 1º de julho. As rotinas preventivas e os planos de saúde
 * não usam esta constante, e sim a janela própria do módulo de cada um.
 */
export const JANELA_DE_VENCIMENTO_EM_DIAS = 183;

/** Quantos milissegundos tem um dia, para a diferença entre duas datas. */
const MILISSEGUNDOS_POR_DIA = 86_400_000;

/** O que um módulo entrega para ser apurado. */
export interface EntradaDeVencimento {
  /** Identificador do registro, para servir de chave na lista. */
  id: string;
  /**
   * A que o registro se refere, escrito por extenso.
   *
   * É a pessoa num documento e numa apólice, e a casa numa rotina de
   * manutenção. O nome é genérico porque o aviso junta módulos diferentes: um
   * campo chamado `pessoa` guardando o nome de um imóvel obrigaria quem lê a
   * descobrir, pelo módulo de onde veio, o que aquele texto significa.
   */
  referente: string;
  /** Como o registro se chama no aviso, já com o rótulo de tela. */
  rotulo: string;
  /** A validade em ISO, ou `null` no registro que não vence. */
  validade: string | null;
  /** A rota da tela onde o registro é resolvido. */
  destino: string;
  /**
   * O que fazer, no lugar do prazo, para o registro que só entra depois de
   * vencido — "Atualizar foto da carteirinha".
   *
   * Com ele, a linha do aviso abre com o lembrete em destaque e a validade
   * anterior entre parênteses, e não repete a validade no fim. Sem ele, a linha
   * abre com `prazoPorExtenso` e termina com a validade.
   */
  lembrete?: string;
}

/** Um item do aviso de vencimento. */
export interface ItemVencendo extends EntradaDeVencimento {
  /** A validade em ISO; aqui nunca é nula, porque quem não tem foi descartado. */
  validade: string;
  /** Dias até vencer; negativo quando já venceu. */
  dias: number;
  /** Verdadeiro quando a validade já passou. */
  vencido: boolean;
}

/**
 * Conta quantos dias faltam de uma data até outra, em dias inteiros.
 *
 * As duas datas são lidas à meia-noite UTC, e não no fuso de quem abre a tela:
 * comparar `2026-12-31` com o instante local faria o mesmo registro aparecer
 * com contagens diferentes conforme o fuso, e mudar de dia ao cruzar a
 * meia-noite em algum lugar do mundo.
 *
 * @param deIso - A data de partida, em `AAAA-MM-DD`.
 * @param ateIso - A data de chegada, em `AAAA-MM-DD`.
 * @returns A diferença em dias; negativa quando a chegada já passou.
 */
export function diasEntre(deIso: string, ateIso: string): number {
  const de = Date.parse(`${deIso}T00:00:00Z`);
  const ate = Date.parse(`${ateIso}T00:00:00Z`);
  return Math.round((ate - de) / MILISSEGUNDOS_POR_DIA);
}

/**
 * Apura o que entra no aviso, do que foi entregue.
 *
 * Registro sem validade é descartado, e não tratado como vencido: há o que não
 * vence por natureza e há o que ninguém localizou e por isso não tem data —
 * anunciar os dois como vencidos encheria o aviso todo dia, e a pessoa pararia
 * de olhar.
 *
 * O que já venceu **entra**: o teste é `dias <= janelaEmDias`, e `dias` é
 * negativo para o que passou. Um registro vencido há dois anos continua no
 * aviso, com a contagem para trás, porque não deixa de ser um problema por ter
 * vencido faz tempo. O último dia da janela também entra: com 183 dias, o
 * registro que vence daqui a 183 dias aparece, e o de 184 não.
 *
 * @param entradas - Os registros do módulo, já mapeados e com o rótulo de tela.
 * @param hojeIso - A data de hoje em `AAAA-MM-DD`, de quem chama.
 * @param janelaEmDias - Quantos dias antes de vencer o registro entra no aviso:
 *   `JANELA_DE_VENCIMENTO_EM_DIAS` para documento, a constante do módulo de
 *   manutenções para rotina preventiva, e um número negativo para o que só
 *   entra depois de vencido — com `-1`, a partir do dia seguinte à validade.
 * @returns Os itens a anunciar, na ordem em que foram entregues.
 */
export function apurarVencimentos(
  entradas: readonly EntradaDeVencimento[],
  hojeIso: string,
  janelaEmDias: number,
): ItemVencendo[] {
  const apurados: ItemVencendo[] = [];

  for (const entrada of entradas) {
    if (entrada.validade === null) continue;

    const dias = diasEntre(hojeIso, entrada.validade);
    if (dias > janelaEmDias) continue;

    apurados.push({
      ...entrada,
      validade: entrada.validade,
      dias,
      vencido: dias < 0,
    });
  }

  return apurados;
}

/**
 * Põe os itens do aviso em ordem de urgência.
 *
 * O mais vencido vem primeiro, e depois o que vence mais cedo: a ordem é pelo
 * `dias`, que é negativo para o que já passou. Devolve uma cópia, porque a
 * lista que chega costuma ser montada na hora por quem chama, e ordenar no
 * lugar mudaria a ordem de um array que não é deste arquivo.
 *
 * @param itens - Os itens apurados, de qualquer número de módulos e em
 *   qualquer ordem.
 * @returns Uma nova lista, do mais urgente ao menos urgente.
 */
export function ordenarPorUrgencia(itens: readonly ItemVencendo[]): ItemVencendo[] {
  return [...itens].sort((a, b) => a.dias - b.dias);
}

/**
 * Escreve o título do aviso, pela pior situação da lista.
 *
 * Basta um item vencido para o título inteiro passar a `⚠ Itens vencidos`; a
 * contagem não entra. A palavra é "Itens", e não o nome de um módulo, porque a
 * lista junta documentos, planos de saúde e rotinas preventivas.
 *
 * @param itens - Os itens apurados; a ordem não importa.
 * @returns `⚠ Itens vencidos` quando há algum vencido, e
 *   `⚠ Itens vencendo em breve` no resto.
 */
export function tituloDoAviso(itens: readonly ItemVencendo[]): string {
  return itens.some((item) => item.vencido) ? '⚠ Itens vencidos' : '⚠ Itens vencendo em breve';
}

/**
 * Escreve há quanto tempo o item venceu, ou quanto falta para vencer — ou, no
 * item com `lembrete`, o que fazer.
 *
 * O plural é resolvido — `1 dia`, `12 dias` —, e o item que vence no próprio
 * dia sai como `Vence hoje`, e não como `Vence em 0 dias`. O item com
 * `lembrete` sai com o lembrete, sem contagem nenhuma: a carteirinha vencida
 * há quarenta dias pede a mesma coisa que a vencida ontem.
 *
 * @param item - O item apurado, com `dias` já contado.
 * @returns O `lembrete`, quando houver; senão `Vencido há N dia(s)`,
 *   `Vence em N dia(s)` ou `Vence hoje`, com o plural certo no lugar do `(s)`.
 */
export function prazoPorExtenso(item: ItemVencendo): string {
  if (item.lembrete !== undefined) return item.lembrete;
  if (item.dias === 0) return 'Vence hoje';

  const dias = Math.abs(item.dias);
  const unidade = dias === 1 ? 'dia' : 'dias';
  return item.vencido
    ? `Vencido há ${String(dias)} ${unidade}`
    : `Vence em ${String(dias)} ${unidade}`;
}

/**
 * Diz se a validade já passou.
 *
 * Existe para as tabelas, que marcam a linha vencida sem precisar da apuração
 * inteira. Ter a comparação aqui é o que impede o mesmo registro de aparecer
 * vencido no aviso da tela inicial e em dia na tabela do módulo, que é o que
 * acontece quando cada tela escreve a sua.
 *
 * @param validade - A validade em ISO, ou `null` no registro que não vence.
 * @param hojeIso - A data de hoje em `AAAA-MM-DD`.
 * @returns Verdadeiro só quando há validade e ela ficou para trás.
 */
export function jaVenceu(validade: string | null, hojeIso: string): boolean {
  return validade !== null && diasEntre(hojeIso, validade) < 0;
}

/**
 * Devolve a data de hoje em `AAAA-MM-DD`, pelo fuso de quem abre a tela.
 *
 * Fica separada de `apurarVencimentos` justamente para que aquela função não
 * dependa do relógio, e possa ser exercida com qualquer data.
 *
 * @returns A data de hoje, no formato que `apurarVencimentos` espera.
 */
export function hojeEmIso(): string {
  const agora = new Date();
  const mes = String(agora.getMonth() + 1).padStart(2, '0');
  const dia = String(agora.getDate()).padStart(2, '0');
  return `${String(agora.getFullYear())}-${mes}-${dia}`;
}
